import type { CallConfiguration as WsCallConfiguration } from "@storyboard/web-client";
import { compareAsc } from "@/store/compare";
import { store } from "@/store/store";
import type { CallConfiguration, CallState } from "@/store/types";

export type CallConfigurationState = CallState;

function transformWsCallConfiguration(record: WsCallConfiguration, state: CallState): CallConfiguration {
  return {
    id: record.callId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    fromAlias: record.from.alias,
    fromAccountId: record.from.accountId,
    fromToken: record.from.token,
    fromDisplayName: record.from.displayName,
    toAlias: record.to.alias,
    toAccountId: record.to.accountId,
    toToken: record.to.token,
    toDisplayName: record.to.displayName,
    initiate: record.initiate,
    state,
  };
}

export async function createCallConfiguration(record: WsCallConfiguration, state: "queued" | "ringing") {
  // The old insert was ON CONFLICT DO NOTHING — an existing configuration wins.
  return store.upsert("callConfiguration", [transformWsCallConfiguration(record, state)], (existing) => existing);
}

export const fetchCallConfigurations = async (): Promise<CallConfiguration[]> => {
  const queued: CallConfiguration[] = [];
  for (const config of store.getAll("callConfiguration").values()) {
    if (config.state === "queued") queued.push(config);
  }
  queued.sort((a, b) => compareAsc(a.createdAt, b.createdAt));
  return queued.slice(0, 2);
};

export async function updateCallState(id: string, state: CallConfigurationState): Promise<void> {
  store.update("callConfiguration", id, (row) => ({ ...row, state }));
}

export async function bulkUpsertCallConfiguration(records: WsCallConfiguration[]) {
  if (records.length === 0) return;
  // The old ON CONFLICT SET replaced everything except createdAt.
  store.upsert(
    "callConfiguration",
    records.map((record) => transformWsCallConfiguration(record, "queued")),
    (existing, next) => ({ ...next, createdAt: existing.createdAt }),
  );
}
